import { parsePhoneNumberFromString, type CountryCode } from 'libphonenumber-js';
import { formatPhoneNumberIntl } from 'react-phone-number-input';

export interface DeviceContact {
  id: string;
  name: string;
  phone: string;
  countryCode?: string;
}

export interface WebContactPick {
  name?: string[];
  tel?: string[];
}

interface BrowserContactSelect {
  select: (
    properties: Array<'name' | 'tel' | 'email' | 'address' | 'icon'>,
    options?: { multiple?: boolean }
  ) => Promise<WebContactPick[]>;
}

declare global {
  interface Navigator {
    contacts?: BrowserContactSelect;
  }
}

export function webContactsUnsupportedMessage(): string {
  const ua = navigator.userAgent;
  const isIos =
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  if (isIos) {
    return 'iPhone browsers cannot read contacts. Open this page in the Milora app.';
  }
  return 'This browser cannot read contacts. Open this page in the Milora app, or in Chrome on Android.';
}

export type WebContactLoadResult =
  | { status: 'unsupported' }
  | { status: 'cancelled' }
  | { status: 'ok'; contacts: DeviceContact[] }
  | { status: 'error'; message: string };

export function flattenWebContactPicks(picks: WebContactPick[]): DeviceContact[] {
  const contacts: DeviceContact[] = [];

  picks.forEach((pick, pickIndex) => {
    const name = pick.name?.map((part) => part.trim()).find(Boolean) || 'Unknown';
    (pick.tel ?? []).forEach((phone, phoneIndex) => {
      const trimmed = phone.trim();
      if (!trimmed.replace(/\D/g, '')) return;
      contacts.push({
        id: `web:${pickIndex}:${phoneIndex}`,
        name,
        phone: trimmed,
      });
    });
  });

  return dedupeDeviceContacts(contacts);
}

export async function loadWebContacts(): Promise<WebContactLoadResult> {
  const contactsManager = navigator.contacts;
  if (!contactsManager?.select) return { status: 'unsupported' };

  try {
    const picks = await contactsManager.select(['name', 'tel'], { multiple: false });
    return { status: 'ok', contacts: flattenWebContactPicks(picks) };
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      return { status: 'cancelled' };
    }
    return {
      status: 'error',
      message: error instanceof Error && error.message ? error.message : 'Could not read contacts.',
    };
  }
}

const REGION_CODE = /^[A-Z]{2}$/;

function asCountry(value: string | undefined): CountryCode | undefined {
  if (!value) return undefined;
  const upper = value.trim().toUpperCase();
  if (!REGION_CODE.test(upper)) return undefined;
  return upper as CountryCode;
}

export function contactPhoneToE164(phone: string, countryCode?: string): string | null {
  const trimmed = phone.trim();
  if (!trimmed) return null;

  const region = asCountry(countryCode);
  const direct = parsePhoneNumberFromString(trimmed, region);
  if (direct?.isValid()) return direct.number;

  const digits = trimmed.replace(/\D/g, '');
  if (!digits) return null;

  if (trimmed.startsWith('+') || trimmed.startsWith('00')) {
    const international = parsePhoneNumberFromString(`+${digits.replace(/^00/, '')}`);
    if (international?.isValid()) return international.number;
  }

  const national = parsePhoneNumberFromString(digits, region ?? 'IN');
  if (national?.isValid()) return national.number;

  return null;
}

export function formatContactPhone(phone: string, countryCode?: string): string {
  const e164 = contactPhoneToE164(phone, countryCode);
  if (!e164) return phone.trim();
  return formatPhoneNumberIntl(e164) || e164;
}

function contactIdentity(contact: DeviceContact): string | null {
  const e164 = contactPhoneToE164(contact.phone, contact.countryCode);
  const digits = (e164 ?? contact.phone).replace(/\D/g, '').replace(/^0+/, '');
  if (!digits) return null;
  const name = contact.name.trim().toLowerCase().replace(/\s+/g, ' ');
  return `${name}|${digits}`;
}

export function dedupeDeviceContacts(contacts: DeviceContact[]): DeviceContact[] {
  const seen = new Set<string>();
  const unique: DeviceContact[] = [];

  for (const contact of contacts) {
    const identity = contactIdentity(contact);
    if (!identity || seen.has(identity)) continue;
    seen.add(identity);
    unique.push({
      ...contact,
      id: identity,
      phone: formatContactPhone(contact.phone, contact.countryCode),
    });
  }

  return unique;
}

export function parseDeviceContacts(value: unknown): DeviceContact[] {
  if (!Array.isArray(value)) return [];
  const contacts: DeviceContact[] = [];

  for (const item of value) {
    if (!item || typeof item !== 'object') continue;
    const record = item as Record<string, unknown>;
    const id = typeof record.id === 'string' ? record.id : '';
    const name = typeof record.name === 'string' ? record.name.trim() : '';
    const phone = typeof record.phone === 'string' ? record.phone.trim() : '';
    const countryCode =
      typeof record.countryCode === 'string' ? record.countryCode.trim() : undefined;
    if (!id || !phone) continue;
    contacts.push({
      id,
      name: name || 'Unknown',
      phone,
      ...(countryCode ? { countryCode } : {}),
    });
  }

  return dedupeDeviceContacts(contacts);
}

export function contactMatchesQuery(contact: DeviceContact, query: string): boolean {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) return true;
  if (contact.name.toLowerCase().includes(trimmed)) return true;
  const digits = trimmed.replace(/\D/g, '');
  if (!digits) return false;
  return contact.phone.replace(/\D/g, '').includes(digits);
}

export function contactInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0 || parts[0] === 'Unknown') return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  const first = parts[0]?.[0] ?? '';
  const last = parts[parts.length - 1]?.[0] ?? '';
  return `${first}${last}`.toUpperCase();
}
